import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./payment_link.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";

interface Employee {
  _id: string;
  email: string;
  password: string;
  role: string;
  name: string;
  phoneNumber: string;
  gender: string;
  __v: number;
}

interface SubscriptionPlan {
  _id: string;
  planName: string;
  description: string;
  mrp: number;
  price: number;
  durationInMonths: number;
  discount: number;
}

const PaymentLink = () => {
  // --- User/Auth State ---
  const [userDetails, setUserDetails] = useState<any>(null);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  
  // --- Payment & Plans State ---
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState<boolean>(true);
  
  const [formData, setFormData] = useState({
    planName: "",
    description: "", // Hidden field to store description
    amount: "",
    planDuration: "",
    customerPhone: "",
    customerName: ""
  });
  
  const [generatedLink, setGeneratedLink] = useState<string>("");
  const [linkLoading, setLinkLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  // --- Fetch User & Plans ---
  const fetchData = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");

      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };

      // 1. Fetch Employee Details
      const userRes = await axios.get(
        `https://api.fitoja.in/api/get-sales-employee-details/${email}`,
        config
      );
      setUserDetails(userRes.data);
      setRole(userRes.data.role);

      // 2. Fetch Subscription Plans
      const plansRes = await axios.get(
        "https://api.fitoja.in/api/get-all-subscription-plans"
      );
      
      if (plansRes.data && plansRes.data.success) {
        setPlans(plansRes.data.data);
      }

      setLoading(false);
      setPlansLoading(false);

    } catch (error) {
      console.error("Failed to fetch data:", error);
      alert("Session expired or network error! Redirecting to login.");
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchData();
      isInitialRender.current = false;
    }
  }, []);

  // --- Form Handlers ---

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Special handler for Plan Dropdown
  const handlePlanSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedPlanName = e.target.value;
    const selectedPlan = plans.find((p) => p.planName === selectedPlanName);

    if (selectedPlan) {
      setFormData({
        ...formData,
        planName: selectedPlan.planName,
        description: selectedPlan.description,
        // OPTIONAL: We pre-fill these for convenience, but user can still edit them manually
        amount: selectedPlan.price.toString(), 
        planDuration: `${selectedPlan.durationInMonths} Months` 
      });
    } else {
      // Reset if "Select Plan" is chosen
      setFormData({ ...formData, planName: "", description: "", amount: "", planDuration: "" });
    }
  };

  const handleGenerateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkLoading(true);
    setGeneratedLink(""); 

    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.post(
        "https://api.fitoja.in/api/admin/create-payment-link",
        formData,
        config
      );

      if (response.data && response.data.short_url) {
        setGeneratedLink(response.data.short_url);
      }
    } catch (error) {
      console.error("Error generating link:", error);
      alert("Failed to generate link. Please check console for details.");
    } finally {
      setLinkLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      alert("Link copied to clipboard!");
    }
  };

  return (
    <div className="employee-container">
      {/* Loading Screen */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Header */}
          <header className="payment-link-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} alt="Fitoja Logo" />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {/* Profile Overlay */}
          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                  alt="Close"
                />
                <p><strong>Name:</strong> {userDetails.name}</p>
                <p><strong>Email:</strong> {userDetails.email}</p>
                <p><strong>Phone Number:</strong> {userDetails.phoneNumber}</p>
                <p><strong>Role:</strong> {userDetails.role}</p>
                <p><strong>Gender:</strong> {userDetails.gender}</p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="payment-link-content">
            <h2>Generate Payment Link</h2>

            <form onSubmit={handleGenerateLink} className="payment-form">
              
              {/* Plan Name Dropdown */}
              <div className="form-group">
                <label>Select Plan</label>
                <select
                  name="planName"
                  value={formData.planName}
                  onChange={handlePlanSelect}
                  required
                  disabled={plansLoading}
                >
                  <option value="">-- Choose a Subscription Plan --</option>
                  {plans.map((plan) => (
                    <option key={plan._id} value={plan.planName}>
                      {plan.planName}
                    </option>
                  ))}
                </select>
                {plansLoading && <small>Loading plans...</small>}
              </div>

              <div className="form-group">
                <label>Amount (INR)</label>
                <input
                  type="number"
                  name="amount"
                  placeholder="e.g. 2999"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Plan Duration</label>
                <input
                  type="text"
                  name="planDuration"
                  placeholder="e.g. 3 Months"
                  value={formData.planDuration}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Client Mobile Number</label>
                <input
                  type="tel"
                  name="customerPhone"
                  placeholder="e.g. +919876543210"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={linkLoading}
              >
                {linkLoading ? "Generating Link..." : "Generate Payment Link"}
              </button>
            </form>

            {/* Result Section */}
            {generatedLink && (
              <div className="result-box">
                <h4>Payment Link Ready!</h4>
                <a 
                  href={generatedLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="generated-url"
                >
                  {generatedLink}
                </a>
                <button onClick={copyToClipboard} className="copy-btn">
                  Copy Link
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentLink;